// What happens to a room's lights when what they are fixed to changes: the room
// copied, a wall split by a new corner or taken out with one, a bench or a
// backrest split in two, the room mirrored. One row per kind of light
// (`LIGHT_LIFE`), so a new kind does not compile until it says what each does.
// Pure: it reads and writes the room's `lights`; ids come from the caller.

import type {
  ProductInstance,
  RoomLight,
  Vec2,
} from '../shared/types';

type LightOf<K extends RoomLight['kind']> = Extract<RoomLight, { kind: K }>;

/** One kind of light through each edit of what it is fixed to. */
interface KindLife<L extends RoomLight> {
  /** The room copied: `ids` maps its features' old ids (benches, backrests, …) to
   *  the copy's. */
  copied(l: L, ids: ReadonlyMap<string, string>): void;
  /** Wall `W` split into `W` and `W + 1` by a new corner; later walls shift up. */
  wallSplit(l: L, W: number): void;
  /** Wall `wall` taken out; later walls shift down, `walls` are left. */
  wallRemoved(l: L, wall: number, walls: number): void;
  /** Its bench or backrest `from` split in two, the second piece `to`: the light
   *  it leaves on `to` (its id still its own), or null. */
  hostSplit(l: L, from: string, to: string): L | null;
  /** The room mirrored in its own frame (x → −x). */
  mirrored(l: L): void;
}

const mirror = (p: Vec2): Vec2 => ({ x: -p.x, y: p.y });
/** Wall numbers after wall `W` split: `W` on both halves, a later one up. */
const splitWalls = (walls: number[], W: number): number[] => walls.flatMap((w) => (w > W ? [w + 1] : w === W ? [W, W + 1] : [w]));
/** Wall numbers after wall `wall` went: it leaves them, a later one down. */
const removedWalls = (walls: number[], wall: number, left: number): number[] => walls.filter((w) => w !== wall).map((w) => (w > wall ? w - 1 : w)).filter((w) => w < left);
const none = (): void => {};

export const LIGHT_LIFE: { [K in RoomLight['kind']]: KindLife<LightOf<K>> } = {
  // A strip runs along a bench or a backrest: a copy lights the copy's own, a
  // split host lights both pieces. Its walls are its host's.
  strip: {
    copied(l, ids) {
      const h = l.host;
      l.host = 'bench' in h ? { bench: ids.get(h.bench) ?? h.bench } : { backrest: ids.get(h.backrest) ?? h.backrest };
    },
    wallSplit: none,
    wallRemoved: none,
    hostSplit(l, from, to) {
      const h = l.host;
      if ('bench' in h) return h.bench === from ? { ...l, host: { bench: to } } : null;
      return h.backrest === from ? { ...l, host: { backrest: to } } : null;
    },
    mirrored: none,
  },
  // A spot is a point of the ceiling.
  spot: {
    copied: none,
    wallSplit: none,
    wallRemoved: none,
    hostSplit: () => null,
    mirrored(l) { l.at = mirror(l.at); },
  },
  // A sky covers the ceiling, or a zone drawn on it; fibres dragged by hand are points.
  sky: {
    copied: none,
    wallSplit: none,
    wallRemoved: none,
    hostSplit: () => null,
    mirrored(l) {
      if (l.zone) l.zone = l.zone.map(mirror);
      if (l.moved) for (const k of Object.keys(l.moved)) l.moved[+k] = mirror(l.moved[+k]);
    },
  },
  // A cove runs along the walls it names: a split wall's both halves, a wall gone
  // leaves it (with none left it goes: `staleLights`).
  cove: {
    copied: none,
    wallSplit(l, W) { l.walls = splitWalls(l.walls, W); },
    wallRemoved(l, wall, walls) { l.walls = removedWalls(l.walls, wall, walls); },
    hostSplit: () => null,
    mirrored: none,
  },
  // A channel is a path of the ceiling; one laid along walls follows them as a
  // cove does, and with none left it is a drawn one.
  channel: {
    copied: none,
    wallSplit(l, W) { if (l.anchor && !l.anchor.closed) l.anchor.walls = splitWalls(l.anchor.walls, W); },
    wallRemoved(l, wall, walls) {
      if (!l.anchor || l.anchor.closed) return;
      l.anchor.walls = removedWalls(l.anchor.walls, wall, walls);
      if (!l.anchor.walls.length) delete l.anchor;
    },
    hostSplit: () => null,
    mirrored(l) { l.pts = l.pts.map(mirror); },
  },
};

/** A light's row of `LIGHT_LIFE`. */
function lifeOf(l: RoomLight): KindLife<RoomLight> {
  return LIGHT_LIFE[l.kind] as KindLife<RoomLight>;
}

/** A copied room's lights: each a new id (`uid`), fixed to the copy's own
 *  features (`ids`: the originals' ids to the copy's). */
export function remintLights(inst: ProductInstance, ids: ReadonlyMap<string, string>, uid: (prefix: string) => string): void {
  for (const l of inst.lights ?? []) {
    l.id = uid('lt');
    lifeOf(l).copied(l, ids);
  }
}

/** Wall `W` of the room split into `W` and `W + 1`. */
export function lightsOnWallSplit(inst: ProductInstance, W: number): void {
  for (const l of inst.lights ?? []) lifeOf(l).wallSplit(l, W);
}

/** Wall `wall` of the room taken out, `walls` left. */
export function lightsOnWallRemoved(inst: ProductInstance, wall: number, walls: number): void {
  for (const l of inst.lights ?? []) lifeOf(l).wallRemoved(l, wall, walls);
}

/** A bench or a backrest `from` split in two, the second piece `to`: what lit
 *  `from` lights `to` too, each twin under the id `newId` gives it. */
export function lightsOnHostSplit(inst: ProductInstance, from: string, to: string, newId: (id: string) => string): void {
  const twins = (inst.lights ?? []).flatMap((l) => {
    const twin = lifeOf(l).hostSplit(l, from, to);
    return twin ? [{ ...twin, id: newId(l.id) }] : [];
  });
  if (twins.length) inst.lights!.push(...twins);
}

/** The room mirrored in its own frame (x → −x): its lights and their moved tags. */
export function mirrorRoomLights(inst: ProductInstance): void {
  for (const l of inst.lights ?? []) {
    if (l.tagOffset) l.tagOffset = mirror(l.tagOffset);
    lifeOf(l).mirrored(l);
  }
}

// ---- Fibre kits and their projector part ----------------------------------------

/** A fibre kit: a starry sky, or a 1-fibre point — fed by a projector part. */
export type FibreKit = LightOf<'sky'> | LightOf<'spot'>;
export const isFibreKit = (l: RoomLight): l is FibreKit => l.kind === 'sky' || (l.kind === 'spot' && !!l.fibre);

/** Copied rooms' fibre kits feed the copy of their projector, copied along
 *  (`partIds`: the drawing's old part ids to the new) — else none: a copy never
 *  shares its original's. */
export function relinkKits(rooms: readonly ProductInstance[], partIds: ReadonlyMap<string, string>): void {
  for (const room of rooms) for (const l of room.lights ?? []) {
    if (!isFibreKit(l) || !l.projectorId) continue;
    const to = partIds.get(l.projectorId);
    if (to) l.projectorId = to; else delete l.projectorId;
  }
}

/** A fibre kit whose projector part is gone (`parts`: the drawing's part ids)
 *  feeds from none; of two naming one, the first keeps it. */
export function unshareKits(rooms: readonly ProductInstance[], parts: ReadonlySet<string>): void {
  const fed = new Set<string>();
  for (const room of rooms) for (const l of room.lights ?? []) {
    if (!isFibreKit(l) || !l.projectorId) continue;
    if (!parts.has(l.projectorId) || fed.has(l.projectorId)) delete l.projectorId; else fed.add(l.projectorId);
  }
}

/** The projector parts the fibre kits of these rooms name. */
export function kitProjectorIds(rooms: readonly ProductInstance[]): string[] {
  return rooms.flatMap((r) => (r.lights ?? []).filter(isFibreKit).map((l) => l.projectorId ?? '')).filter(Boolean);
}
